import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RotateCcw, HeartPulse } from "lucide-react";

function getInterpretation(si) {
  if (si < 0.6) return { label: "Normal", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30", guidance: "No hemodynamic compromise" };
  if (si < 0.8) return { label: "Borderline", color: "bg-yellow-500", textColor: "text-yellow-500", bgColor: "bg-yellow-500/10 border-yellow-500/30", guidance: "Monitor closely" };
  if (si < 1.0) return { label: "Elevated", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30", guidance: "Suspect early shock — assess volume status" };
  if (si < 1.4) return { label: "High", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", guidance: "Significant hemodynamic compromise — aggressive resuscitation" };
  return { label: "Critical", color: "bg-red-700", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", guidance: "Severe shock — immediate intervention required" };
}

export default function ShockIndexCalculator() {
  const [hr, setHr] = useState("");
  const [sbp, setSbp] = useState("");
  const [open, setOpen] = useState(false);

  const hasBoth = hr && sbp && parseFloat(hr) > 0 && parseFloat(sbp) > 0;
  const si = hasBoth ? (parseFloat(hr) / parseFloat(sbp)).toFixed(2) : null;
  const interp = si ? getInterpretation(parseFloat(si)) : null;

  const handleReset = () => { setHr(""); setSbp(""); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="shock-index-trigger">
          <HeartPulse className="w-3.5 h-3.5" />
          SI
          {si && <Badge className={`${interp.color} text-white text-[10px] ml-1`}>{si}</Badge>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <HeartPulse className="w-5 h-5" /> Shock Index Calculator
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">Shock Index = Heart Rate ÷ Systolic BP</p>

        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Heart Rate (bpm)</label>
              <Input
                type="number"
                placeholder="80"
                value={hr}
                onChange={(e) => setHr(e.target.value)}
                className="font-mono"
                data-testid="si-hr-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">SBP (mmHg)</label>
              <Input
                type="number"
                placeholder="120"
                value={sbp}
                onChange={(e) => setSbp(e.target.value)}
                className="font-mono"
                data-testid="si-sbp-input"
              />
            </div>
          </div>

          {/* Result */}
          <div className={`rounded-lg border p-4 ${si ? interp.bgColor : 'bg-muted/30'}`} data-testid="si-result">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide">Shock Index</span>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-3xl font-bold font-mono ${si ? interp.textColor : 'text-muted-foreground'}`} data-testid="si-value">
                    {si || "--"}
                  </span>
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-1">
                {si && <Badge className={`${interp.color} text-white`}>{interp.label}</Badge>}
                <Button variant="ghost" size="sm" onClick={handleReset} className="h-6 text-xs px-2" data-testid="si-reset">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            </div>
            {si && (
              <div className="mt-2 pt-2 border-t border-current/10">
                <p className={`font-semibold text-sm ${interp.textColor}`} data-testid="si-guidance">{interp.guidance}</p>
              </div>
            )}
          </div>

          {/* Reference */}
          <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
            <p className="font-medium">Reference Ranges:</p>
            <div className="grid grid-cols-2 gap-1">
              <span>Normal:</span><span className="text-green-500 font-mono">&lt; 0.6</span>
              <span>Borderline:</span><span className="text-yellow-500 font-mono">0.6–0.79</span>
              <span>Elevated:</span><span className="text-amber-500 font-mono">0.8–0.99</span>
              <span>High:</span><span className="text-red-500 font-mono">1.0–1.39</span>
              <span>Critical:</span><span className="text-red-400 font-mono">≥ 1.4</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
